import React from "react";

import classNames from "classnames";

import DropzoneListItem, {
  DROPZONE_MEDIA_ITEM_HEIGHT,
} from "./DropzoneListItem";

import type { JSX } from "react";

import type {
  DropzoneItem,
  DropzoneRenderItem,
  InitialDropzoneItem,
} from "./DropzoneListContainer";
import type { DropzoneMoveItemParams } from "../../types/fast";

interface DropzoneItemListProps<T> {
  acceptList: string[];
  itemList: (T & DropzoneItem)[];
  onDeleteClick: (uuid: string) => void;
  onMoveItem: ({ itemUuid, newRank, newItem }: DropzoneMoveItemParams) => void;
  isDropDisabled?: boolean;
  isDragDisabled?: boolean;
  renderItem: DropzoneRenderItem<T>;
  isFullSize?: boolean;
}

function DropzoneItemList<T extends InitialDropzoneItem>({
  acceptList,
  itemList,
  onDeleteClick,
  onMoveItem,
  isDropDisabled,
  isDragDisabled,
  renderItem,
  isFullSize,
}: DropzoneItemListProps<T>): JSX.Element {
  // L'ordre DOM doit rester stable : réordonner les nœuds réinitialiserait leurs
  // transitions CSS. La position visuelle est dérivée du rang via `displayRank`
  // (les items sont en position absolute, positionnés par `transform`).
  const displayRankByUuid = new Map(
    [...itemList]
      .sort((a, b) => a.rank - b.rank)
      .map((sortedItem, index) => [sortedItem.uuid, index]),
  );

  const containerStyle = !isFullSize
    ? {
        height: `${DROPZONE_MEDIA_ITEM_HEIGHT * itemList.length - 10}px`,
        transition: "height 0.2s",
        willChange: "height",
      }
    : {};

  const containerClassName = classNames(
    "relative w-full",
    isFullSize && "h-full",
  );

  return (
    <div className={containerClassName} style={containerStyle}>
      {itemList.map((item) => (
        <DropzoneListItem
          key={item.uuid}
          acceptList={acceptList}
          displayRank={displayRankByUuid.get(item.uuid) ?? 0}
          isDragDisabled={isDragDisabled ?? itemList.length === 1}
          isNewMediaDropDisabled={isDropDisabled}
          item={item}
          onDeleteClick={onDeleteClick}
          onMoveItem={onMoveItem}
          renderItem={renderItem}
        />
      ))}
    </div>
  );
}

export default DropzoneItemList;
